import { useState } from 'react';
import { Box, Chip, Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import {
  CheckCircleOutline,
  ErrorOutline,
  EventAvailableOutlined,
  EventBusyOutlined,
  EventRepeatOutlined,
  KeyboardArrowDown,
  LinkOutlined,
  OpenInNew,
  PendingOutlined,
  PlayCircleOutline,
  SkipNextOutlined,
} from '@mui/icons-material';

import { ProfilePageId } from 'views/profile/profile-content.types';
import {
  ContactRole,
  ContactRoleApplicationRoutingDecision,
  ContactRoleInterviewType,
  ContactRoleTrackStatus,
  ContactRoleVettingStep,
  ContactRoleVettingStepOutcome,
  ContactRoleVettingStepStatus,
  ContactRoleVettingStepType,
} from 'types/domain';
import RoleStatusChip, { RoleStatusChipConfig } from './role-track-status-chip';
import { formatTimestamp, formatValueLabel } from './role-track.utils';

const statusConfigMap: Record<ContactRoleVettingStepStatus, StepStatusChipConfig> = {
  [ContactRoleVettingStepStatus.Started]: {
    backgroundColor: 'rgba(33, 150, 243, 0.08)',
    iconColor: '#2196F3',
    icon: PlayCircleOutline,
  },
  [ContactRoleVettingStepStatus.InProgress]: {
    backgroundColor: 'rgba(33, 150, 243, 0.08)',
    iconColor: '#2196F3',
    icon: PendingOutlined,
  },
  [ContactRoleVettingStepStatus.BookingLinkSent]: {
    backgroundColor: 'rgba(33, 150, 243, 0.08)',
    iconColor: '#2196F3',
    icon: LinkOutlined,
  },
  [ContactRoleVettingStepStatus.Booked]: {
    backgroundColor: 'rgba(33, 150, 243, 0.08)',
    iconColor: '#2196F3',
    icon: EventAvailableOutlined,
  },
  [ContactRoleVettingStepStatus.Rescheduled]: {
    backgroundColor: 'rgba(237, 108, 2, 0.08)',
    iconColor: '#FFB547',
    icon: EventRepeatOutlined,
  },
  [ContactRoleVettingStepStatus.Canceled]: {
    backgroundColor: 'rgba(244, 67, 54, 0.08)',
    iconColor: '#F44336',
    icon: EventBusyOutlined,
  },
  [ContactRoleVettingStepStatus.Completed]: {
    backgroundColor: 'rgba(97, 97, 97, 0.08)',
    iconColor: '#757575',
    icon: CheckCircleOutline,
  },
  [ContactRoleVettingStepStatus.Error]: {
    backgroundColor: 'rgba(244, 67, 54, 0.08)',
    iconColor: '#F44336',
    icon: ErrorOutline,
  },
  [ContactRoleVettingStepStatus.Skipped]: {
    backgroundColor: 'rgba(97, 97, 97, 0.08)',
    iconColor: '#757575',
    icon: SkipNextOutlined,
  },
};

const outcomeConfigMap: Record<ContactRoleVettingStepOutcome, RoleStatusChipConfig> = {
  [ContactRoleVettingStepOutcome.Pass]: {
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    color: '#2E7D32',
  },
  [ContactRoleVettingStepOutcome.Reject]: {
    backgroundColor: 'rgba(211, 47, 47, 0.08)',
    color: '#C62828',
  },
  [ContactRoleVettingStepOutcome.Retake]: {
    backgroundColor: 'rgba(237, 108, 2, 0.1)',
    color: '#B45309',
  },
  [ContactRoleVettingStepOutcome.Unclear]: {
    backgroundColor: 'rgba(237, 108, 2, 0.1)',
    color: '#B45309',
  },
  [ContactRoleVettingStepOutcome.NotDetermined]: {
    backgroundColor: 'rgba(97, 97, 97, 0.08)',
    color: '#616161',
  },
  [ContactRoleVettingStepOutcome.None]: {
    backgroundColor: 'rgba(97, 97, 97, 0.08)',
    color: '#616161',
  },
};

const outcomeLabelMap: Record<ContactRoleVettingStepOutcome, string> = {
  [ContactRoleVettingStepOutcome.Pass]: 'Pass',
  [ContactRoleVettingStepOutcome.Reject]: 'Reject',
  [ContactRoleVettingStepOutcome.Retake]: 'Retake',
  [ContactRoleVettingStepOutcome.Unclear]: 'Unclear',
  [ContactRoleVettingStepOutcome.NotDetermined]: 'Not determined',
  [ContactRoleVettingStepOutcome.None]: 'No outcome',
};

const routingDecisionConfigMap: Record<ContactRoleApplicationRoutingDecision, RoleStatusChipConfig> = {
  [ContactRoleApplicationRoutingDecision.Pass]: {
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    color: '#2E7D32',
  },
  [ContactRoleApplicationRoutingDecision.NeedReview]: {
    backgroundColor: 'rgba(237, 108, 2, 0.1)',
    color: '#B45309',
  },
  [ContactRoleApplicationRoutingDecision.Reject]: {
    backgroundColor: 'rgba(211, 47, 47, 0.08)',
    color: '#C62828',
  },
};

const trackStatusConfigMap: Record<ContactRoleTrackStatus, RoleStatusChipConfig> = {
  [ContactRoleTrackStatus.VettingNotStarted]: {
    backgroundColor: 'rgba(97, 97, 97, 0.08)',
    color: '#616161',
  },
  [ContactRoleTrackStatus.InProgress]: {
    backgroundColor: 'rgba(33, 150, 243, 0.08)',
    color: '#1976D2',
  },
  [ContactRoleTrackStatus.Completed]: {
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    color: '#2E7D32',
  },
  [ContactRoleTrackStatus.Error]: {
    backgroundColor: 'rgba(211, 47, 47, 0.08)',
    color: '#C62828',
  },
};

const trackStatusLabelMap: Record<ContactRoleTrackStatus, string> = {
  [ContactRoleTrackStatus.VettingNotStarted]: 'Vetting not started',
  [ContactRoleTrackStatus.InProgress]: 'In progress',
  [ContactRoleTrackStatus.Completed]: 'Completed',
  [ContactRoleTrackStatus.Error]: 'Error',
};

const typeLabelMap: Record<ContactRoleVettingStepType, string> = {
  [ContactRoleVettingStepType.VoiceAgentCall]: 'Mark',
  [ContactRoleVettingStepType.Coderbyte]: 'Coderbyte',
  [ContactRoleVettingStepType.Calendly]: 'Interview',
};

export const RoleRoutingSummary = ({ role }: { role: ContactRole }) => {
  const trackStatusChip = role.isVerified ? (
    <RoleStatusChip label="Verified" config={trackStatusConfigMap[ContactRoleTrackStatus.Completed]} />
  ) : role.trackStatus ? (
    <RoleStatusChip label={trackStatusLabelMap[role.trackStatus]} config={trackStatusConfigMap[role.trackStatus]} />
  ) : null;

  if (!role.applicationRoutingDecision) {
    if (!role.vettingSteps?.length) {
      return trackStatusChip || <RoleStatusChip label="Vetting not started" />;
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75, flexWrap: 'wrap' }}>
        {trackStatusChip}
        {groupVettingStepAttempts(role.vettingSteps).map(({ key, attempts: [step] }) => (
          <VettingStepStatusChip key={key} step={step} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75, flexWrap: 'wrap' }}>
      {role.isVerified ? (
        trackStatusChip
      ) : (
        <RoleStatusChip
          label={formatValueLabel(role.applicationRoutingDecision)}
          config={routingDecisionConfigMap[role.applicationRoutingDecision]}
        />
      )}
    </Box>
  );
};

export const VettingStepStatusChip = ({ step }: { step: ContactRoleVettingStep }) => (
  <StepStatusChip
    label={formatCompactStepName(step)}
    config={statusConfigMap[step.status]}
    tooltip={`Status: ${formatStepStatusLabel(step)}`}
    ariaLabel={`${formatCompactStepName(step)}: ${formatStepStatusLabel(step)}`}
  />
);

export const VettingStepAttempts = ({ attempts }: VettingStepAttemptsProps) => {
  const [expanded, setExpanded] = useState(false);
  const [latestAttempt, ...previousAttempts] = attempts;

  return (
    <Box>
      <VettingStep
        step={latestAttempt}
        attemptCount={attempts.length}
        attemptsExpanded={expanded}
        onToggleAttempts={previousAttempts.length ? () => setExpanded((value) => !value) : undefined}
      />
      {previousAttempts.length ? (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ pl: 2, borderLeft: '2px solid #F5F5F5' }}>
            {previousAttempts.map((step) => (
              <VettingStep
                key={step.id || `${step.type}-${step.interviewType || 'default'}-${step.attempt}`}
                step={step}
                attemptCount={attempts.length}
              />
            ))}
          </Box>
        </Collapse>
      ) : null}
    </Box>
  );
};

export const VettingStep = ({
  step,
  attemptCount = 1,
  attemptsExpanded = false,
  onToggleAttempts,
}: VettingStepProps) => {
  const aiCallLink =
    step.type === ContactRoleVettingStepType.VoiceAgentCall && step.voiceAgentCallId
      ? `?tab=${ProfilePageId.AI_CALLS}&callId=${encodeURIComponent(step.voiceAgentCallId)}`
      : null;
  const link = aiCallLink || step.link;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) auto' },
        alignItems: 'center',
        gap: 2,
        minHeight: 72,
        borderTop: '1px solid #F5F5F5',
        py: 1.25,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {step.name || typeLabelMap[step.type] || step.type}
          </Typography>
          {link ? (
            <Tooltip title={getStepLinkLabel(step, Boolean(aiCallLink))}>
              <IconButton
                component="a"
                href={link}
                {...(!aiCallLink && { target: '_blank', rel: 'noreferrer' })}
                color="secondary"
                size="small"
                sx={{ p: 0.5 }}
              >
                <OpenInNew sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          ) : null}
          <Typography component="span" variant="caption" color="text.disabled">
            ·
          </Typography>
          <StepProgressStatus step={step} />
          {attemptCount > 1 ? (
            <>
              <Typography component="span" variant="caption" color="text.disabled">
                · Attempt {step.attempt} of {attemptCount}
              </Typography>
              {onToggleAttempts ? (
                <Tooltip
                  title={attemptsExpanded ? 'Hide previous attempts' : `Show ${attemptCount - 1} previous attempts`}
                >
                  <IconButton
                    aria-expanded={attemptsExpanded}
                    aria-label={attemptsExpanded ? 'Hide previous attempts' : 'Show previous attempts'}
                    size="small"
                    onClick={onToggleAttempts}
                    sx={{
                      p: 0.25,
                      color: 'text.secondary',
                      transition: 'transform 120ms ease',
                      transform: attemptsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  >
                    <KeyboardArrowDown sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              ) : null}
            </>
          ) : null}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {formatStepTimestamp(step)}
        </Typography>
        {step.status === ContactRoleVettingStepStatus.Skipped ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Note: {getSkippedStepNote(step)}
          </Typography>
        ) : null}
      </Box>
      {step.outcome ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            gap: 0.75,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Outcome
          </Typography>
          <RoleStatusChip label={outcomeLabelMap[step.outcome]} config={outcomeConfigMap[step.outcome]} />
        </Box>
      ) : null}
    </Box>
  );
};

const StepProgressStatus = ({ step }: { step: ContactRoleVettingStep }) => {
  const content = (
    <Typography
      component="span"
      variant="caption"
      color="text.secondary"
      aria-label={`Progress status: ${formatStepStatusLabel(step)}`}
    >
      {formatValueLabel(formatStepStatusLabel(step))}
    </Typography>
  );

  return step.errorReason ? <Tooltip title={`Technical error: ${step.errorReason}`}>{content}</Tooltip> : content;
};

const StepStatusChip = ({ label, config, tooltip, ariaLabel }: StepStatusChipProps) => {
  const StatusIcon = config?.icon;
  const chip = (
    <Chip
      aria-label={ariaLabel}
      size="small"
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box component="span">{label}</Box>
          {StatusIcon ? <StatusIcon sx={{ color: config.iconColor, fontSize: 16 }} /> : null}
        </Box>
      }
      sx={{
        height: 24,
        ...(config ? { '&.MuiChip-root': { backgroundColor: config.backgroundColor } } : {}),
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );

  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
};

const getStepLinkLabel = (step: ContactRoleVettingStep, isAiCallLink: boolean) => {
  if (isAiCallLink) return 'Open call in AI calls';
  return step.type === ContactRoleVettingStepType.Coderbyte ? 'Open Coderbyte report' : 'Open link';
};

const formatStepTimestamp = (step: ContactRoleVettingStep) => {
  const timestamp = step.completedAt || step.lastActivityAt || step.startedAt;
  return timestamp ? formatTimestamp(timestamp) : 'No timestamp';
};

const formatStepStatusLabel = (step: ContactRoleVettingStep) => {
  if (step.type !== ContactRoleVettingStepType.Calendly) {
    return step.status;
  }

  const calendlyStatusLabels: Partial<Record<ContactRoleVettingStepStatus, string>> = {
    [ContactRoleVettingStepStatus.BookingLinkSent]: 'booking link sent',
    [ContactRoleVettingStepStatus.Booked]: 'booked',
    [ContactRoleVettingStepStatus.Rescheduled]: 'rescheduled',
    [ContactRoleVettingStepStatus.Canceled]: 'canceled',
  };

  return calendlyStatusLabels[step.status] || step.status;
};

const formatCompactStepName = (step: ContactRoleVettingStep) => {
  if (step.type === ContactRoleVettingStepType.VoiceAgentCall) return 'Mark call';
  if (step.type === ContactRoleVettingStepType.Calendly && step.interviewType) {
    return formatValueLabel(step.interviewType);
  }

  return typeLabelMap[step.type] || step.name || step.type;
};

const getSkippedStepNote = (step: ContactRoleVettingStep) =>
  step.type === ContactRoleVettingStepType.Calendly && step.interviewType === ContactRoleInterviewType.ScreeningCall
    ? 'The recruiter invited the candidate directly to the Hard Skills interview.'
    : 'The recruiter invited the candidate before this step was completed.';

export const groupVettingStepAttempts = (steps: ContactRoleVettingStep[] = []): VettingStepAttemptGroup[] => {
  const groups = new Map<string, ContactRoleVettingStep[]>();

  steps.forEach((step) => {
    const key = `${step.type}-${step.interviewType || 'default'}`;
    groups.set(key, [...(groups.get(key) || []), step]);
  });

  return Array.from(groups, ([key, attempts]) => ({
    key,
    attempts: attempts.sort((first, second) => second.attempt - first.attempt),
  }));
};

export const getLatestVettingStep = (steps: ContactRoleVettingStep[] = []) =>
  steps.reduce<ContactRoleVettingStep | null>((latestStep, step) => {
    if (!latestStep) return step;

    return getVettingStepTimestamp(step) >= getVettingStepTimestamp(latestStep) ? step : latestStep;
  }, null);

const getVettingStepTimestamp = (step: ContactRoleVettingStep) =>
  Math.max(
    ...[step.completedAt, step.lastActivityAt, step.startedAt].map((value) => (value ? new Date(value).getTime() : 0))
  );

type VettingStepAttemptGroup = {
  key: string;
  attempts: ContactRoleVettingStep[];
};

type VettingStepAttemptsProps = {
  attempts: ContactRoleVettingStep[];
};

type VettingStepProps = {
  step: ContactRoleVettingStep;
  attemptCount?: number;
  attemptsExpanded?: boolean;
  onToggleAttempts?: () => void;
};

type StepStatusChipConfig = {
  backgroundColor: string;
  iconColor: string;
  icon: typeof CheckCircleOutline;
};

type StepStatusChipProps = {
  label: string;
  config?: StepStatusChipConfig;
  tooltip?: string | null;
  ariaLabel?: string;
};
